/**
 * 案内に出す「その操作ができる役職」の呼び名（issue #480・決定: 案B）。
 *
 * 役職の呼び名は会社ごとに変えられます（issue #433。例:「本部管理者」→「本社総務」）。
 * 画面の案内に初期の呼び名を決め打ちせず、**その操作ができる役職の、いまの会社での呼び名**で言います。
 *
 * **呼び名はサーバから受け取ります**（ログインのときに読む `GET /org/roles` の役職の一覧。
 * できることは、受け持ちの範囲で効かないものをサーバが外したあとの値）。文の作り方は
 * サーバ（server/src/auth/role-wording.ts）と同じで、shared/role-wording-cases.json の例で
 * 両方が同じ文になることを試験で確かめています。どちらかを直すときは、両方と表をそろえてください。
 *
 * サーバが断ったときの文（画面にそのまま出すもの）は、サーバが会社の呼び名で作って返します。
 * ここで作るのは、画面が自分で出す案内（操作の前に出す注記・断られたあとの次の一手）だけです。
 */
import { useMemo } from 'react';
import type { RolesResponse } from '../api/types.js';
import { useSession } from './session-context.js';

/** その操作ができる役職の条件（できること＋受け持ちの範囲）。 */
export interface RoleRequirement {
  /** 要るできること（すべて持つ役職が当てはまる） */
  readonly capabilities: readonly string[];
  /** 受け持ちの範囲が「全社」の役職だけか */
  readonly orgScope?: boolean;
}

/** 呼び名を引くのに要る、役職1つ分の中身（`GET /org/roles` の1行）。 */
export interface RoleNaming {
  readonly display_name: string;
  readonly capabilities: Readonly<Record<string, boolean>>;
  readonly scope: 'org' | 'site';
}

/** 呼び名を差し込む材料（その会社の役職の一覧と、できることの名前）。 */
export interface RoleWordingSource {
  readonly roles: readonly RoleNaming[];
  /** できることのキー → 画面に出す名前 */
  readonly labels: Readonly<Record<string, string>>;
}

/** 当てはまる役職の呼び名を「・」で並べたもの。無ければ null。 */
export function roleNamesText(
  source: RoleWordingSource,
  requirement: RoleRequirement,
): string | null {
  const names = [
    ...new Set(
      source.roles
        .filter(
          (role) =>
            (requirement.orgScope !== true || role.scope === 'org') &&
            requirement.capabilities.every((capability) => role.capabilities[capability] === true),
        )
        .map((role) => role.display_name.trim())
        .filter((name) => name !== ''),
    ),
  ];
  return names.length === 0 ? null : names.join('・');
}

/** 条件を言葉にしたもの（当てはまる役職が無いときに使う）。 */
export function requirementText(source: RoleWordingSource, requirement: RoleRequirement): string {
  const labels = requirement.capabilities.map(
    (capability) =>
      `「${(source.labels[capability] ?? capability).replace(/（[^（）]*）$/u, '')}」`,
  );
  const what = `${labels.join('と')}ができる`;
  return requirement.orgScope === true ? `受け持ちの範囲が「全社」で、${what}` : what;
}

/** 「誰が」の言い方（「本社総務の方」／「「◯◯」ができる役職の方」）。 */
export function whoCan(source: RoleWordingSource, requirement: RoleRequirement): string {
  const names = roleNamesText(source, requirement);
  return names === null ? `${requirementText(source, requirement)}役職の方` : `${names}の方`;
}

/** 「◯◯のは、本社総務の方だけです。」（lead は「のは」で終える） */
export function onlyRolesSentence(
  source: RoleWordingSource,
  requirement: RoleRequirement,
  lead: string,
): string {
  const sentence = `${lead}、${whoCan(source, requirement)}だけです。`;
  return roleNamesText(source, requirement) === null
    ? `${sentence}いまは、当てはまる役職がありません。`
    : sentence;
}

/** 「必要なときは、本社総務の方へご依頼ください。」（無ければ「いまは、…役職がありません。」） */
export function askRolesSentence(
  source: RoleWordingSource,
  requirement: RoleRequirement,
  options: { lead?: string; others?: boolean } = {},
): string {
  const names = roleNamesText(source, requirement);
  if (names === null) return `いまは、${requirementText(source, requirement)}役職がありません。`;
  return `${options.lead ?? '必要なときは'}、${options.others === true ? 'ほかの' : ''}${names}の方へご依頼ください。`;
}

/** `GET /org/roles` の答えから、呼び名を差し込む材料を作る。 */
export function roleWordingSource(roles: RolesResponse | null | undefined): RoleWordingSource {
  return {
    roles: roles?.roles ?? [],
    labels: Object.fromEntries(
      (roles?.capabilities ?? []).map((capability) => [capability.key, capability.description]),
    ),
  };
}

/** 画面で使う形（その会社の役職の一覧に結び付けたもの）。 */
export interface RoleWording {
  names: (requirement: RoleRequirement) => string | null;
  who: (requirement: RoleRequirement) => string;
  only: (requirement: RoleRequirement, lead: string) => string;
  ask: (requirement: RoleRequirement, options?: { lead?: string; others?: boolean }) => string;
}

export function bindRoleWording(source: RoleWordingSource): RoleWording {
  return {
    names: (requirement) => roleNamesText(source, requirement),
    who: (requirement) => whoCan(source, requirement),
    only: (requirement, lead) => onlyRolesSentence(source, requirement, lead),
    ask: (requirement, options) => askRolesSentence(source, requirement, options),
  };
}

/** ログイン中の会社の役職の呼び名で案内を作る。 */
export function useRoleWording(): RoleWording {
  const { profile } = useSession();
  const roles = profile?.roles;
  return useMemo(() => bindRoleWording(roleWordingSource(roles)), [roles]);
}

/**
 * よく使う条件（どれも受け持ちの範囲とできることだけで決める。役職のキーや呼び名では決めない）。
 */
export const ROLE_REQUIREMENTS = {
  /** 役職・拠点を変える／全社の名簿を扱う（「名簿を管理する」＋全社） */
  assignRoles: { capabilities: ['member.manage'], orgScope: true },
  /** 会社の保存期間・1対1の「事故対応」・無期限への変更（「保存期間を変える」＋全社） */
  orgRetention: { capabilities: ['retention.change'], orgScope: true },
  /** トークの保存期間・「事故対応」の指定を変える */
  retention: { capabilities: ['retention.change'] },
  /** 名簿をCSVでまとめて登録する（一括の取り込みと名簿の管理の両方が要る） */
  rosterImport: { capabilities: ['roster.manage', 'member.manage'] },
  /** 全社の名簿をCSVで登録する（ほかの拠点の方の行） */
  rosterImportOrgWide: { capabilities: ['roster.manage', 'member.manage'], orgScope: true },
  /** 一人ひとりの既読・未読を取り出す */
  exportReadReceipts: { capabilities: ['data.export'] },
  /** 名簿を管理する（参加者の追加・名簿を見る） */
  memberManage: { capabilities: ['member.manage'] },
  /** トークを管理する（受け持ちの範囲のトーク） */
  channelManage: { capabilities: ['channel.manage_any'] },
  /** 答えない領域を変える（「AI先輩の答えを登録・承認する」＋全社） */
  restrictedTopics: { capabilities: ['ai_answer.approve'], orgScope: true },
  /** 役職のできることを変える */
  roleSettings: { capabilities: ['settings.manage'] },
  /** 受け持ちの範囲が「全社」の役職を変える */
  orgRoleSettings: { capabilities: ['settings.manage'], orgScope: true },
} as const satisfies Record<string, RoleRequirement>;
